import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  Stack,
  Divider,
  IconButton
} from '@mui/material';
import { X as CloseIcon } from 'lucide-react';
import languageLearningApi from '../../middleware/languageLearningApi';

const CourseletForm = ({ sectionId, courselet, defaultOrder, onClose, onCascadeUpdate, existingCourselets }) => {
  const [title, setTitle] = useState(courselet ? courselet.title : '');
  const [domain, setDomain] = useState(courselet ? courselet.domain : '');
  const [order, setOrder] = useState(courselet ? courselet.order : defaultOrder || 1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Only in creation mode, validate that order is unique.
    if (!courselet && existingCourselets && existingCourselets.some(c => c.order === order)) {
      setErrors(prev => ({ ...prev, order: 'An existing courselet with this order exists.' }));
      console.log("Validation error: duplicate order");
      setIsSubmitting(false);
      return;
    }
    
    const courseletData = { title, domain, order, sectionId };
    
    try {
      // If editing and order has changed, perform cascade update first.
      if (courselet && courselet.order !== order && onCascadeUpdate) {
        await onCascadeUpdate(order, courselet._id);
      }
      
      let response;
      if (courselet && courselet._id) {
        response = await languageLearningApi.put(`/courselets/${courselet._id}`, courseletData);
      } else {
        response = await languageLearningApi.post(`/courselets/${sectionId}/courselets`, courseletData);
      }
      const data = response.data;
      if (data.success) {
        onClose();
      }
    } catch (err) {
      console.error("Error saving courselet:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper 
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
        position: 'relative',
        maxWidth: '100%'
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h3" fontWeight="medium">
          {courselet ? 'Edit Courselet' : 'Add Courselet'}
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon size={18} />
        </IconButton>
      </Box>
      
      <Divider sx={{ mb: 3 }} />
      
      <form onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            label="Title"
            variant="outlined"
            size="small"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            placeholder="Enter courselet title"
          />
          
          <TextField
            fullWidth
            label="Domain"
            variant="outlined"
            size="small"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            required
            placeholder="Enter domain"
          />
          
          <TextField
            fullWidth
            label="Order"
            variant="outlined"
            type="number"
            size="small"
            value={order}
            onChange={(e) => {
              const newOrder = parseInt(e.target.value) || 1;
              setOrder(newOrder);
              // In creation mode, check for duplicates.
              if (!courselet && existingCourselets && existingCourselets.some(c => c.order === newOrder)) {
                setErrors(prev => ({ ...prev, order: 'An existing courselet with this order exists.' }));
              } else {
                setErrors(prev => {
                  const newErrors = { ...prev };
                  delete newErrors.order;
                  return newErrors;
                });
              }
            }}
            required
            InputProps={{ inputProps: { min: 1 } }}
            error={Boolean(errors.order)}
            helperText={errors.order}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Stack>
      </form>
    </Paper>
  );
};

export default CourseletForm;
